import {
  AlertTriangle,
  BookOpen,
  ChevronDown,
  Layers,
  Link2,
  Loader2,
  Menu,
  Moon,
  Power,
  Save,
  Sparkles,
  Sun,
  X
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Section2_Contenu from '../components/AtelierCreator/SectionContenu';
import Section1_Identite from '../components/AtelierCreator/SectionIdentite';
import Section3_Ressources from '../components/AtelierCreator/SectionRessources';
import useAtelierCreator from '../stores/useAtelierCreator';

// ── DESIGN TOKENS ── adaptés pour AtelierCreator (couleurs indigo/cyan/slate)
const tokens = (dm) => ({
  bg: dm ? 'bg-zinc-950' : 'bg-white',
  bgSub: dm ? 'bg-zinc-900/60' : 'bg-zinc-50',
  border: dm ? 'border-white/8' : 'border-zinc-200',
  text: dm ? 'text-zinc-100' : 'text-zinc-900',
  textSub: dm ? 'text-zinc-400' : 'text-zinc-500',
  textMuted: dm ? 'text-zinc-600' : 'text-zinc-400',
  input: dm
    ? 'bg-zinc-900 border-zinc-700 text-zinc-100 placeholder:text-zinc-600 focus:border-indigo-400/60 focus:ring-1 focus:ring-indigo-400/20'
    : 'bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 shadow-sm',
  accent: 'text-indigo-500',
  accentBg: dm ? 'bg-indigo-500/10' : 'bg-indigo-50',
  accentBorder: dm ? 'border-indigo-500/25' : 'border-indigo-200',
});

// ── SECTIONS ─────────────────────────────────────────────────────────────
const SECTIONS = [
  {
    id: 1,
    label: 'Identité',
    labelExpert: '01 Identité',
    icon: Sparkles,
    color: 'indigo',
    sub: 'Slug · Tags · Mapping',
  },
  {
    id: 2,
    label: 'Contenu',
    labelExpert: '02 Contenu',
    icon: BookOpen,
    color: 'cyan',
    sub: 'Slides · Exercices',
  },
  {
    id: 3,
    label: 'Ressources',
    labelExpert: '03 Ressources',
    icon: Link2,
    color: 'slate',
    sub: 'Liens · PDF · Live',
  },
];

// ── Navigation latérale (MissionControl like) ───────────────────────────
function MissionControl({ sections, currentSection, setSection, darkMode, interfaceMode, onToggleNav }) {
  const { ateliersList, loadAtelier, resetForm, deleteAtelier, ui } = useAtelierCreator();
  const dm = darkMode;
  const isSimple = interfaceMode === 'simplifie';

  const sectionGuide = {
    1: { emoji: '🏷️', what: 'Définissez l\'identité de votre atelier', why: 'La base de tout le scénario pédagogique', tip: 'Un bon slug est court et sans espaces.' },
    2: { emoji: '📚', what: 'Créez le contenu théorique et les exercices', why: 'Le cœur pédagogique de l\'atelier', tip: 'Structurez vos slides avant les exercices.' },
    3: { emoji: '🔗', what: 'Ajoutez les ressources et configurez le live', why: 'Les supports complémentaires', tip: 'Les PDF sont téléchargeables par les apprenants.' },
  };

  const guide = sectionGuide[currentSection];
  const currentS = sections.find(s => s.id === currentSection);

  return (
    <div className={`flex flex-col h-full ${dm ? 'bg-zinc-950' : 'bg-white'}`}>
      {/* Logo avec bouton fermeture */}
      <div className={`p-5 flex justify-between items-center ${dm ? 'border-white/6' : 'border-zinc-100'}`}>
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-indigo-500 rounded-lg flex items-center justify-center shadow-lg shadow-indigo-500/25">
            <Sparkles size={14} className="text-white" />
          </div>
          <div>
            <div className={`text-[12px] font-bold ${dm ? 'text-white' : 'text-zinc-900'}`}>Constructeur PESA</div>
            <div className={`text-[10px] ${dm ? 'text-zinc-500' : 'text-zinc-400'}`}>
              {isSimple ? 'Assistant guidé' : 'Architecture d\'atelier'}
            </div>
          </div>
        </div>
        <button
          onClick={onToggleNav}
          className={`p-1.5 rounded-md transition-colors ${dm ? 'text-zinc-500 hover:text-white hover:bg-zinc-800' : 'text-zinc-400 hover:text-zinc-900 hover:bg-zinc-200'}`}
        >
          <X size={16} />
        </button>
      </div>

      {/* Barre de progression */}
      <div className={`px-5 py-4 ${dm ? 'border-white/6' : 'border-zinc-100'}`}>
        <div className="flex items-center justify-between mb-2">
          <span className={`text-[11px] font-medium ${dm ? 'text-zinc-500' : 'text-zinc-400'}`}>Avancement</span>
          <span className={`text-[12px] font-bold ${dm ? 'text-indigo-400' : 'text-indigo-600'}`}>{currentSection}/3</span>
        </div>
        <div className={`h-2 rounded-full overflow-hidden ${dm ? 'bg-zinc-800' : 'bg-zinc-200'}`}>
          <div
            className="h-full rounded-full bg-indigo-500 transition-all duration-700"
            style={{ width: `${(currentSection / 3) * 100}%` }}
          />
        </div>
      </div>

      {/* Sections */}
      <div className="px-3 py-4 flex-1 overflow-y-auto">
        <p className={`text-[10px] font-semibold uppercase tracking-[0.2em] mb-3 px-2 ${dm ? 'text-zinc-600' : 'text-zinc-400'}`}>Étapes</p>

        <div className="space-y-1">
          {sections.map((s) => {
            const isActive = currentSection === s.id;
            const Icon = s.icon;
            return (
              <button
                key={s.id}
                onClick={() => setSection(s.id)}
                className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl transition-all text-left ${
                  isActive
                    ? dm ? 'bg-indigo-500/12 border border-indigo-500/20' : 'bg-indigo-50 border border-indigo-200'
                    : dm ? 'hover:bg-zinc-900 border border-transparent' : 'hover:bg-zinc-50 border border-transparent'
                }`}
              >
                <div className={`w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  isActive
                    ? 'bg-indigo-500 text-white'
                    : dm ? 'bg-zinc-800 text-zinc-500' : 'bg-zinc-100 text-zinc-400'
                }`}>
                  <Icon size={12} />
                </div>
                <div className="flex-1 min-w-0">
                  <span className={`text-[12px] font-semibold ${isActive ? dm ? 'text-indigo-300' : 'text-indigo-700' : dm ? 'text-zinc-400' : 'text-zinc-600'}`}>
                    {isSimple ? s.label : s.labelExpert}
                  </span>
                  <p className={`text-[10px] mt-0.5 truncate ${dm ? 'text-zinc-600' : 'text-zinc-400'}`}>{s.sub}</p>
                </div>
                {isActive && <ChevronDown size={12} className="text-indigo-400 flex-shrink-0" />}
              </button>
            );
          })}
        </div>

        {/* Guide contextuel */}
        {guide && (
          <div className={`mt-5 mx-1 p-4 rounded-xl border ${dm ? 'border-indigo-500/15 bg-indigo-500/5' : 'border-indigo-100 bg-indigo-50/50'}`}>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-base">{guide.emoji}</span>
              <span className={`text-[11px] font-semibold ${dm ? 'text-indigo-300' : 'text-indigo-700'}`}>{currentS?.label}</span>
            </div>
            <p className={`text-[11px] font-medium mb-1 ${dm ? 'text-zinc-300' : 'text-zinc-700'}`}>{guide.what}</p>
            <p className={`text-[11px] mb-2 ${dm ? 'text-zinc-500' : 'text-zinc-500'}`}>{guide.why}</p>
            <div className={`pt-2  text-[10px] italic ${dm ? 'border-white/5 text-zinc-600' : 'border-indigo-100 text-zinc-400'}`}>
              💡 {guide.tip}
            </div>
          </div>
        )}
      </div>

      {/* Liste ateliers existants */}
      <div className={`px-4 py-4  ${dm ? 'border-white/6' : 'border-zinc-100'}`}>
        <div className="flex items-center justify-between mb-2">
          <p className={`text-[10px] font-mono font-bold tracking-[0.2em] uppercase ${dm ? 'opacity-40' : 'text-zinc-400'}`}>
            Ateliers ({ateliersList.length})
          </p>
          <button
            onClick={resetForm}
            className={`text-[9px] font-mono uppercase tracking-wider transition-colors px-2 py-0.5 rounded border ${dm ? 'text-indigo-400/70 hover:text-indigo-300 border-indigo-500/30 hover:border-indigo-500/50' : 'text-indigo-600 hover:text-indigo-700 border-indigo-300 hover:border-indigo-400'}`}
          >
            + Nouveau
          </button>
        </div>

        <div className="max-h-36 overflow-y-auto space-y-1">
          {ateliersList.slice(0, 5).map(a => {
            const isEditing = ui.editingId === (a._id || a.id);
            return (
              <div
                key={a._id || a.id}
                className={`group flex items-center gap-2 px-2 py-1.5 border cursor-pointer transition-all rounded ${
                  isEditing
                    ? dm ? 'border-indigo-500/40 bg-indigo-500/5' : 'border-indigo-300 bg-indigo-50'
                    : dm ? 'border-white/5 hover:border-white/20' : 'border-zinc-200 hover:border-zinc-300'
                }`}
              >
                <span className="text-sm flex-shrink-0">{a.icon || '🎓'}</span>
                <span
                  className={`flex-1 text-[10px] font-mono truncate ${dm ? 'text-slate-300' : 'text-zinc-700'}`}
                  onClick={() => loadAtelier(a)}
                >
                  {a.title || a.slug || 'Sans titre'}
                </span>
                <button
                  onClick={() => {
                    if (window.confirm(`Supprimer "${a.title}" ?`)) {
                      deleteAtelier(a._id || a.id);
                    }
                  }}
                  className={`opacity-0 group-hover:opacity-100 transition-all ${dm ? 'text-red-500/60 hover:text-red-400' : 'text-red-400 hover:text-red-600'}`}
                >
                  <X size={10} />
                </button>
              </div>
            );
          })}
          {ateliersList.length > 5 && (
            <p className={`text-[9px] font-mono text-center ${dm ? 'opacity-30' : 'text-zinc-400'}`}>+{ateliersList.length - 5} autres</p>
          )}
        </div>
      </div>

      {/* Footer mode */}
      <div className={`p-4  ${dm ? 'border-white/6' : 'border-zinc-100'}`}>
        <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${dm ? 'bg-zinc-900' : 'bg-zinc-50'}`}>
          <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
          <span className={`text-[11px] font-medium ${dm ? 'text-zinc-400' : 'text-zinc-600'}`}>
            {isSimple ? 'Mode Simplifié actif' : 'Mode Expert actif'}
          </span>
        </div>
      </div>
    </div>
  );
}

// ── Barre de contexte ───────────────────────────────────────────────────
function WorkspaceContextBar({ sectionLabel, sectionHint, darkMode }) {
  return (
    <div className={`px-6 py-3 flex items-center justify-between ${darkMode ? 'bg-zinc-900/50 border-white/6' : 'bg-zinc-50/80 border-zinc-200'}`}>
      <div className="flex items-center gap-2">
        <span className={`text-[11px] font-medium ${darkMode ? 'text-zinc-500' : 'text-zinc-400'}`}>Constructeur</span>
        <ChevronDown size={10} className={darkMode ? 'text-zinc-700' : 'text-zinc-300'} />
        <span className={`text-[12px] font-semibold ${darkMode ? 'text-indigo-300' : 'text-indigo-600'}`}>{sectionLabel}</span>
      </div>
      {sectionHint && (
        <div className={`hidden md:flex items-center gap-1.5 text-[11px] ${darkMode ? 'text-zinc-500' : 'text-zinc-400'}`}>
          <Sparkles size={11} className="text-indigo-400/60 flex-shrink-0" />
          <span>{sectionHint}</span>
        </div>
      )}
    </div>
  );
}

// ── COMPOSANT PRINCIPAL ─────────────────────────────────────────────────
export default function AtelierCreator() {
  const navigate = useNavigate();
  
  const [localDarkMode, setLocalDarkMode] = useState(true);
  const dm = localDarkMode;
  const tk = tokens(dm);
  const [isSimple, setIsSimple] = useState(false);

  const {
    formData,
    ui,
    errors,
    toggleNav,
    setSection,
    loadAteliersList,
    loadMappings,
    resetForm,
    saveAtelier,
    ateliersList,
  } = useAtelierCreator();

  useEffect(() => {
    loadAteliersList();
    loadMappings();
    resetForm();
  }, []);

  const handleSave = async () => {
    const slug = await saveAtelier();
    if (slug) {
      // optionnel : navigate vers la preview
    }
  };

  const errorCount = Object.keys(errors).length;

  const SectionComponents = {
    1: Section1_Identite,
    2: Section2_Contenu,
    3: Section3_Ressources,
  };
  const CurrentSection = SectionComponents[ui.currentSection];
  const currentSectionInfo = SECTIONS.find(s => s.id === ui.currentSection);

  return (
    <div className={`h-screen flex flex-col ${dm ? 'bg-zinc-950' : 'bg-zinc-50'}`}>

      {/* ════ HEADER — aligné sur CaseCreator ════ */}
      <header className={`sticky top-0 z-50 px-5 py-3 flex justify-between items-center backdrop-blur-xl ${dm ? 'bg-zinc-950/90 border-white/8' : 'bg-white/90 border-zinc-200 shadow-sm'}`}>

        {/* Gauche */}
        <div className="flex items-center gap-4">
          <button
            onClick={toggleNav}
            className={`p-2 rounded-lg transition-all ${ui.isNavOpen ? dm ? 'bg-indigo-500/15 border-indigo-500/30 text-indigo-300' : 'bg-indigo-50 border-indigo-200 text-indigo-600' : dm ? 'border-zinc-800 text-zinc-500 hover:text-zinc-300 hover:border-zinc-700' : 'border-zinc-200 text-zinc-500 hover:bg-zinc-50'}`}
            title="Afficher/masquer le panneau de navigation"
          >
            {ui.isNavOpen ? <X size={16} /> : <Menu size={16} />}
          </button>

          <div className="flex items-center gap-2.5">
            <div className={`p-1.5 rounded-lg border ${dm ? 'bg-indigo-500/10 border-indigo-500/20' : 'bg-indigo-50 border-indigo-200'}`}>
              <Sparkles size={16} className="text-indigo-500" />
            </div>
            <div>
              <div className={`text-[13px] font-bold ${dm ? 'text-white' : 'text-zinc-900'}`}>Constructeur d'atelier</div>
              <div className={`text-[10px] ${dm ? 'text-zinc-500' : 'text-zinc-400'}`}>
                PESA Core — {isSimple ? 'Mode guidé' : 'Mode expert'}
              </div>
            </div>
          </div>

          {/* Section active */}
          {currentSectionInfo && (
            <div className={`hidden lg:flex items-center gap-2 px-3 py-1.5 rounded-lg border ${dm ? 'border-indigo-500/20 bg-indigo-500/8' : 'border-indigo-200 bg-indigo-50'}`}>
              <currentSectionInfo.icon size={12} className="text-indigo-500" />
              <span className={`text-[12px] font-semibold ${dm ? 'text-indigo-300' : 'text-indigo-700'}`}>
                {isSimple ? currentSectionInfo.label : currentSectionInfo.labelExpert}
              </span>
            </div>
          )}
        </div>

        {/* Centre : Templates */}
        <div className="flex-1 max-w-sm mx-8">
          <div className="relative">
            <Layers size={14} className="absolute left-3 inset-y-0 my-auto text-zinc-400 pointer-events-none" />
            <select
              onChange={(e) => {
                if (!e.target.value) return;
                const found = ateliersList.find(a => (a._id || a.id) === e.target.value);
                if (found) useAtelierCreator.getState().loadAtelier(found);
              }}
              value={ui.editingId || ''}
              className={`w-full pl-9 pr-8 py-2 rounded-lg border text-[12px] outline-none appearance-none cursor-pointer transition-all ${
                dm
                  ? 'bg-zinc-900 border-zinc-700 text-zinc-300 focus:border-indigo-400/50'
                  : 'bg-white border-zinc-200 text-zinc-700 focus:border-indigo-300 shadow-sm'
              }`}
            >
              <option value="">Charger un atelier existant...</option>
              {ateliersList.map(a => (
                <option key={a._id || a.id} value={a._id || a.id}>
                  {a.icon} {a.title || a.slug}
                </option>
              ))}
            </select>
            <ChevronDown size={13} className="absolute right-3 inset-y-0 my-auto text-zinc-400 pointer-events-none" />
          </div>
        </div>

        {/* Droite */}
        <div className="flex items-center gap-2">
          {/* Mode simplifié / expert — CORRIGÉ : bouton actif */}
          <button
            onClick={() => setIsSimple(!isSimple)}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-[11px] font-medium transition-all ${isSimple ? dm ? 'bg-cyan-500/10 border-cyan-500/20 text-cyan-400' : 'bg-cyan-50 border-cyan-200 text-cyan-700' : dm ? 'bg-indigo-500/10 border-indigo-500/20 text-indigo-400' : 'bg-indigo-50 border-indigo-200 text-indigo-700'}`}
          >
            {isSimple ? '🎓 Simplifié' : '⚡ Expert'}
          </button>

          {/* Toggle dark/light */}
          <button
            onClick={() => setLocalDarkMode(prev => !prev)}
            className={`p-2 rounded-lg border transition-all ${dm ? 'bg-zinc-800 border-zinc-700 text-yellow-400 hover:bg-zinc-700' : 'bg-white border-zinc-200 text-zinc-600 hover:bg-zinc-50'}`}
            title={dm ? 'Passer en mode clair' : 'Passer en mode sombre'}
          >
            {dm ? <Sun size={16} /> : <Moon size={16} />}
          </button>

          {/* Sauvegarder */}
          <button
            onClick={handleSave}
            disabled={ui.isSaving}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-[12px] font-semibold transition-all ${
              dm
                ? 'bg-indigo-600 hover:bg-indigo-500 text-white'
                : 'bg-indigo-500 hover:bg-indigo-600 text-white shadow-sm'
            } disabled:opacity-40 disabled:cursor-not-allowed`}
          >
            <Save size={14} />
            {ui.isSaving ? 'Sauvegarde...' : 'Sauvegarder'}
          </button>

          {/* Fermer */}
          <button
            onClick={() => {
              if (window.history.length > 1) {
                window.history.back();
              } else {
                window.location.href = '/';
              }
            }}
            className={`p-2 rounded-lg border transition-all ${dm ? 'border-zinc-800 text-zinc-500 hover:border-red-500/40 hover:text-red-400 hover:bg-red-500/5' : 'border-zinc-200 text-zinc-500 hover:border-red-300 hover:text-red-500 hover:bg-red-50'}`}
            title="Fermer"
          >
            <Power size={16} />
          </button>
        </div>
      </header>

      {/* ════ LAYOUT PRINCIPAL ════ */}
      <div className="flex-1 flex overflow-y-auto no-scrollbar">

        {/* Sidebar */}
        {ui.isNavOpen && (
          <aside className={`flex-shrink-0 overflow-hidden transition-all duration-200 ${dm ? 'border-white/6' : 'border-zinc-200'}`} style={{ width: 288 }}>
            <MissionControl
              sections={SECTIONS}
              currentSection={ui.currentSection}
              setSection={setSection}
              darkMode={dm}
              interfaceMode={isSimple ? 'simplifie' : 'expert'}
              onToggleNav={toggleNav}
            />
          </aside>
        )}

        {/* Workspace */}
        <main className={`flex-1 flex flex-col overflow-y-auto no-scrollbar ${dm ? 'bg-zinc-950' : 'bg-zinc-50'}`}>

          {/* Barre de contexte */}
          <WorkspaceContextBar
            sectionLabel={isSimple ? currentSectionInfo?.label || '' : currentSectionInfo?.labelExpert || ''}
            sectionHint={currentSectionInfo?.sub}
            darkMode={dm}
          /> 

          {/* Nav sections horizontale */}
          <div className={`flex items-center gap-0.5 px-6 py-2 ${dm ? 'border-white/5 bg-zinc-950' : 'border-zinc-200 bg-white'}`}>
            {SECTIONS.map((s) => {
              const isActive = ui.currentSection === s.id;
              const Icon = s.icon;
              return (
                <button
                  key={s.id}
                  onClick={() => setSection(s.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-[12px] font-medium transition-all ${
                    isActive
                      ? dm ? 'bg-indigo-500/12 text-indigo-300 border border-indigo-500/20' : 'bg-indigo-50 text-indigo-700 border border-indigo-200'
                      : dm ? 'text-zinc-500 hover:text-zinc-300 border border-transparent' : 'text-zinc-500 hover:text-zinc-700 border border-transparent hover:bg-zinc-100'
                  }`}
                >
                  <Icon size={13} className={isActive ? 'text-indigo-500' : dm ? 'text-zinc-600' : 'text-zinc-400'} />
                  <span>{isSimple ? s.label : s.labelExpert}</span>
                </button>
              );
            })}
          </div>

          {/* Contenu */}
          <div className="flex-1 overflow-y-auto no-scrollbar p-6 md:p-8">
            <div className="max-w-7xl mx-auto">
              {CurrentSection && <CurrentSection darkMode={dm} />}

              {/* ── Footer navigation — CORRIGÉ : sticky bottom-0 pour rester en bas ── */}
              <footer className={`sticky bottom-0 mt-16 pt-8 pb-4  flex items-center justify-between ${dm ? 'border-zinc-800 bg-zinc-950' : 'border-zinc-200 bg-zinc-50'}`}>
                <div className="flex items-center gap-5">
                  <div className={`text-[11px] ${dm ? 'text-zinc-600' : 'text-zinc-400'}`}>
                    <span className="block">Progression</span>
                    <span className={`font-bold text-[14px] ${dm ? 'text-indigo-400' : 'text-indigo-600'}`}>{ui.currentSection}/3</span>
                  </div>
                  <div className={`h-6 w-px ${dm ? 'bg-zinc-800' : 'bg-zinc-200'}`} />
                  <div className={`text-[11px] ${dm ? 'text-zinc-600' : 'text-zinc-400'}`}>
                    <span className="block">Mode</span>
                    <span className={`font-bold text-[13px] ${dm ? 'text-indigo-300' : 'text-indigo-700'}`}>{ui.isEditMode ? 'ÉDITION' : 'CRÉATION'}</span>
                  </div>
                  <div className={`h-6 w-px ${dm ? 'bg-zinc-800' : 'bg-zinc-200'}`} />
                  <div className={`text-[11px] ${dm ? 'text-zinc-600' : 'text-zinc-400'}`}>
                    <span className="block">Validation</span>
                    <span className={`font-bold text-[13px] ${errorCount === 0 ? 'text-emerald-500' : 'text-amber-500'}`}>
                      {errorCount === 0 ? 'Prêt' : 'Incomplet'}
                    </span>
                  </div>
                  {formData.slug && (
                    <>
                      <div className={`h-6 w-px ${dm ? 'bg-zinc-800' : 'bg-zinc-200'}`} />
                      <div className={`text-[11px] ${dm ? 'text-zinc-600' : 'text-zinc-400'}`}>
                        <span className="block">Slug</span>
                        <span className={`font-mono text-[10px] ${dm ? 'text-indigo-400/70' : 'text-indigo-600/70'}`}>
                          /ateliers/{formData.slug}
                        </span>
                      </div>
                    </>
                  )}
                </div>

                <div className="flex gap-3">
                  {ui.currentSection > 1 && (
                    <button
                      type="button"
                      onClick={() => setSection(ui.currentSection - 1)}
                      className={`flex items-center gap-2 px-5 py-2.5 rounded-lg border text-[12px] font-medium transition-all ${dm ? 'border-zinc-700 text-zinc-400 hover:text-white hover:border-zinc-500' : 'border-zinc-300 text-zinc-600 hover:bg-zinc-100'}`}
                    >
                      ← Précédent
                    </button>
                  )}
                  {ui.currentSection < 3 ? (
                    <button
                      type="button"
                      onClick={() => setSection(ui.currentSection + 1)}
                      className="flex items-center gap-2 px-8 py-2.5 bg-indigo-500 hover:bg-indigo-600 text-white text-[12px] font-semibold rounded-lg transition-colors shadow-sm"
                    >
                      Suivant →
                    </button>
                  ) : (
                    <button
                      onClick={handleSave}
                      disabled={ui.isSaving || errorCount > 0}
                      className="flex items-center gap-2 px-8 py-2.5 bg-gradient-to-r from-indigo-500 via-cyan-500 to-indigo-500 text-white text-[12px] font-semibold rounded-lg transition-all disabled:opacity-40 disabled:cursor-not-allowed shadow-sm"
                    >
                      {ui.isSaving ? <><Loader2 size={14} className="animate-spin" /> Déploiement...</> : <><Save size={14} /> Déployer l'atelier</>}
                    </button>
                  )}
                </div>
              </footer>
            </div>
          </div>
        </main>
      </div>

      {/* Erreurs de validation */}
      {errorCount > 0 && (
        <div className={`fixed bottom-6 right-6 z-50 max-w-sm rounded-xl p-4 shadow-2xl backdrop-blur-md animate-in fade-in slide-in-from-bottom-4 ${dm ? 'bg-red-950/90 border border-red-600/40 text-red-200' : 'bg-red-50 border border-red-200 text-red-800'}`}>
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle size={14} className="text-red-400 flex-shrink-0" />
            <span className={`text-[11px] font-bold uppercase tracking-wide ${dm ? 'text-red-400' : 'text-red-600'}`}>
              {errorCount} erreur(s) bloquante(s)
            </span>
          </div>
          <ul className="space-y-1.5 max-h-48 overflow-y-auto text-[11px]">
            {Object.entries(errors).slice(0, 3).map(([path, error]) => (
              <li key={path} className="flex flex-col">
                <span className={`font-semibold ${dm ? 'text-red-300' : 'text-red-700'}`}>{path}</span>
                <span>{error?.message || 'Champ invalide'}</span>
              </li>
            ))}
          </ul>
          <p className={`text-[10px] mt-2 italic ${dm ? 'opacity-60' : 'opacity-50'}`}>Corrigez ces champs pour activer le déploiement</p>
        </div>
      )}
    </div>
  );
}